import _ from 'lodash';

import getLineDefaultConfig from '../LineChartCommonConfig';
import { average, median } from '../../utils/common';
import { trendLine, trendLineConfig } from '../TrendLine';
import updateYaxisPlotLines from '../ChartPlotline';
import {
  complexRetentionByDateChartTooltip,
  complexRetentionChartTooltip,
} from '../../components/tooltip/ComplexRetentionChartTooltip';
import { toggleShowAllButton } from '../Common';

const group = (data) => ({
  categories: data[0].categories.map((item) => `+${item} ${data[0].timeUnit}`),
  series: data.flatMap(({ name, series, timeUnit, id }) =>
    series.map((s) => ({
      ...s,
      timeUnit,
      name: `${id}.${name}`,
    }))
  ),
});

export const toComplexRetentionLineConfig = (data, options = {}, fromDashboard) => {
  const { series, categories } = group(data);
  const chartSeries = series.map((s, index) => {
    const values = s.data.map(([, v]) => v);
    const averageValue = average(values);
    const { min, max } = trendLine(values, averageValue);
    const mapedData = s.data.map(([, y, sum, total]) => ({
      y,
      sum,
      total,
      name: '',
      eventName: s.name,
      uniqueChar: s.parentId,
    }));

    return {
      id: index,
      name: `${s.name}`,
      data: mapedData,
      uniqueChar: s.parentId,
      trendMin: min,
      trendMax: max,
      average: averageValue,
      isNegative: s.data.some((v) => v[1] < 0),
      type: 'line',
    };
  });

  let paths;
  const columnChartDefaultConfig = getLineDefaultConfig(chartSeries, options, fromDashboard);

  return {
    ...columnChartDefaultConfig,
    xAxis: {
      min: null,
      max: null,
      categories,
    },
    yAxis: {
      ...columnChartDefaultConfig.yAxis,
      labels: {
        format: '{value}%',
      },
    },
    series: chartSeries,
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: 'rgba(255,255,255,0.8)',
      formatter(tooltip) {
        const {
          chart: { hoverPoint },
        } = tooltip;
        return complexRetentionChartTooltip(hoverPoint);
      },
    },
    chart: {
      zooming: {
        type: 'x',
      },
      spacingLeft: 0,
      marginLeft: 50,
      events: {
        redraw({ target: chart }) {
          if (!!chart.legend && chart.legend.title !== undefined) {
            chart.legend.title.attr('x', chart.legend.legendWidth / 2);
            chart.legend.title.attr('text-anchor', 'middle');
          }
          if (chart.series) {
            if (chart.showAll !== chart.series.every((s) => s.visible)) {
              toggleShowAllButton(chart);
            }

            // отфильтрованные серии графика, которые отображаются в данный момент
            const chartFilteredSeries = chart.series.filter((s) => s.visible);
            // id отфильтрованных серий
            const ids = chartFilteredSeries.map((s) => s.userOptions.id);
            const plotLines = [];
            // отфильтрованные серии взятые из подготовленного конфига, а не из самих графиков
            const filteredSeries = chartSeries.filter(({ id }) => ids.includes(id));

            // линия тренда
            if (paths) {
              paths.forEach(({ element }) => element.remove());
            }
            if (chart.userOptions.trendLine) {
              const xAxis = chart.xAxis[0];
              const yAxis = chart.yAxis[0];
              paths = chartFilteredSeries.map(({ userOptions }, index) =>
                chart.renderer
                  .path([
                    'M',
                    xAxis.toPixels(xAxis.dataMin),
                    yAxis.toPixels(userOptions.trendMin),
                    'L',
                    xAxis.toPixels(xAxis.dataMax),
                    yAxis.toPixels(userOptions.trendMax),
                  ])
                  .attr({
                    ...trendLineConfig,
                    ...{
                      zIndex: 5,
                      stroke: chartFilteredSeries[index].color,
                    },
                  })
                  .add()
              );
            }

            // plotLines
            if (filteredSeries.some((s) => s.isNegative)) {
              plotLines.push({
                id: 'zeroPlotLine',
                lineColor: 'black',
                dashStyle: 'solid',
                text: '',
                textColor: '',
                value: 0,
              });
            }
            if (chart.userOptions.averageLine) {
              const averages = filteredSeries.map(({ average: averageParam }) => averageParam);
              const averageValue = _.round(average(averages), 2);
              plotLines.push({
                id: 'averagePlotLine',
                lineColor: 'orange',
                dashStyle: 'dash',
                text: `Avg = ${averageValue}%`,
                textColor: 'orange',
                value: averageValue,
              });
            }
            if (chart.userOptions.medianLine) {
              const resultData = filteredSeries.flatMap((d) => d.data.map(({ y }) => y));
              const medianValue = _.round(median(resultData) / filteredSeries.length, 2);
              plotLines.push({
                id: 'medianPlotLine',
                lineColor: 'red',
                dashStyle: 'solid',
                text: `Median = ${medianValue}%`,
                textColor: 'red',
                value: medianValue,
              });
            }
            updateYaxisPlotLines(plotLines, chart);
          }
        },
      },
    },
  };
};

const groupSeries = (data) => ({
  series: data.flatMap(({ name, returnEventName, series, timeUnit, id }) =>
    series.map((s) => ({
      ...s,
      timeUnit,
      timeShift: name,
      name: `${id}.${returnEventName} (+${name} ${timeUnit})`,
    }))
  ),
});

export const toComplexRetentionByDateLineConfig = (data, options = {}, fromDashboard) => {
  const { series } = groupSeries(data);
  const chartSeries = series.map((s, index) => {
    const values = s.data.filter(([, , , total]) => !!total).map(([, v]) => v);
    const averageValue = average(values);
    const { min, max } = trendLine(values, averageValue);
    const resultData = s.data.map(([x, y, sum, total]) => ({
      x,
      y,
      sum,
      total,
      name: '',
      eventName: s.name,
      uniqueChar: s.parentId,
      timeShift: s.timeShift,
      timeUnit: s.timeUnit,
      timezone: options.timezone,
    }));

    return {
      id: index,
      name: `${s.name}`,
      data: resultData,
      uniqueChar: s.parentId,
      trendMin: min,
      trendMax: max,
      average: averageValue,
      isNegative: s.data.some((v) => v[1] < 0),
      type: 'line',
    };
  });

  let paths;
  const lineChartDefaultConfig = getLineDefaultConfig(chartSeries, options, fromDashboard);

  return {
    ...lineChartDefaultConfig,
    xAxis: {
      min: null,
      max: null,
      categories: null,
      type: 'datetime',
    },
    yAxis: {
      ...lineChartDefaultConfig.yAxis,
      labels: {
        format: '{value}%',
      },
    },
    series: chartSeries,
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: 'rgba(255,255,255,0.8)',
      formatter(tooltip) {
        const {
          chart: { hoverPoint },
        } = tooltip;
        return complexRetentionByDateChartTooltip(hoverPoint);
      },
    },
    chart: {
      zooming: {
        type: 'x',
      },
      spacingLeft: 0,
      marginLeft: 50,
      events: {
        redraw({ target: chart }) {
          if (!!chart.legend && chart.legend.title !== undefined) {
            chart.legend.title.attr('x', chart.legend.legendWidth / 2);
            chart.legend.title.attr('text-anchor', 'middle');
          }
          if (chart.series) {
            if (chart.showAll !== chart.series.every((s) => s.visible)) {
              toggleShowAllButton(chart);
            }

            // отфильтрованные серии графика, которые отображаются в данный момент
            const chartFilteredSeries = chart.series.filter((s) => s.visible);
            // id отфильтрованных серий
            const ids = chartFilteredSeries.map((s) => s.userOptions.id);
            const plotLines = [];
            // отфильтрованные серии взятые из подготовленного конфига, а не из самих графиков
            const filteredSeries = chartSeries.filter(({ id }) => ids.includes(id));

            // линия тренда
            if (paths) {
              paths.forEach(({ element }) => element.remove());
            }
            if (chart.userOptions.trendLine) {
              const xAxis = chart.xAxis[0];
              const yAxis = chart.yAxis[0];
              paths = chartFilteredSeries.map(({ userOptions }, index) =>
                chart.renderer
                  .path([
                    'M',
                    xAxis.toPixels(xAxis.dataMin),
                    yAxis.toPixels(userOptions.trendMin),
                    'L',
                    xAxis.toPixels(xAxis.dataMax),
                    yAxis.toPixels(userOptions.trendMax),
                  ])
                  .attr({
                    ...trendLineConfig,
                    ...{
                      zIndex: 5,
                      stroke: chartFilteredSeries[index].color,
                    },
                  })
                  .add()
              );
            }

            // plotLines
            if (filteredSeries.some((s) => s.isNegative)) {
              plotLines.push({
                id: 'zeroPlotLine',
                lineColor: 'black',
                dashStyle: 'solid',
                text: '',
                textColor: '',
                value: 0,
              });
            }
            if (chart.userOptions.averageLine) {
              const averages = filteredSeries.map(({ average: averageParam }) => averageParam);
              const averageValue = _.round(average(averages), 2);
              plotLines.push({
                id: 'averagePlotLine',
                lineColor: 'orange',
                dashStyle: 'dash',
                text: `Avg = ${averageValue}%`,
                textColor: 'orange',
                value: averageValue,
              });
            }
            if (chart.userOptions.medianLine) {
              const resultData = filteredSeries.flatMap((d) =>
                d.data.filter(({ total }) => !!total).map(({ y }) => y)
              );
              const medianValue = _.round(median(resultData) / filteredSeries.length, 2);
              plotLines.push({
                id: 'medianPlotLine',
                lineColor: 'red',
                dashStyle: 'solid',
                text: `Median = ${medianValue}%`,
                textColor: 'red',
                value: medianValue,
              });
            }
            updateYaxisPlotLines(plotLines, chart);
          }
        },
      },
    },
  };
};
